<script lang="ts">

  import { goto } from "$app/navigation"
  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "$lib/components/ui/command"
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from "$lib/components/ui/dropdown-menu"
  import { Input } from "$lib/components/ui/input"
  import { Label } from "$lib/components/ui/label"
  import { Popover, PopoverButton, PopoverContent, PopoverTrigger } from "$lib/components/ui/popover"
  import { type Relation, RelationEmpty } from "$lib/data/schema/_relation"
  import type { _response } from "$lib/data/type/_response"
  import { createFilter } from "$lib/hooks/filter.svelte"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import Calendar1Icon from "@lucide/svelte/icons/calendar-1"
  import EllipsisVerticalIcon from "@lucide/svelte/icons/ellipsis-vertical"
  import FilterIcon from "@lucide/svelte/icons/filter"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import PlusIcon from "@lucide/svelte/icons/plus"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import { createInfiniteQuery, createQuery } from "@tanstack/svelte-query"
  import DialogDetail from "./_components/dialog-detail.svelte"
  import { type Activity } from "./_types"

  const prefix = $derived(page.params.prefix ?? "0")

  const filter = createFilter({
    today: true,
    category: RelationEmpty
  })

  let bottomOfPage = $state<HTMLDivElement>()

  let selected = $state<Activity>()
  const detailDialog = useDialog()

  const categories = createQuery(() => ({
    queryKey: ["activity-plan-category", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/activity/plans/master-category`)
      return response.data.result ?? []
    }
  }))

  const search = createSearch()
  const query = createInfiniteQuery(() => ({
    queryKey: ["activity-plan", {
      prefix,
      today: filter.state.today,
      category_id: filter.state.category.id,
      search: search.debounced
    }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<Activity[]>>(`/api/${prefix}/activity/plans`, {
        params: {
          page: pageParam,
          today: filter.state.today ? 1 : 0,
          category_id: filter.state.category.id,
          search: search.debounced
        }
      })

      return response.data.result ?? []
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))

  const rows = $derived(query.data?.pages.flat() ?? [])

  function handleDetail(activity: Activity) {
    selected = activity
    detailDialog.setOpen(true)
  }

  function handleEdit(activity: Activity) {
    goto(`/${prefix}/activity-plan/${activity.id}`)
  }

  $effect(() => {
    if (!bottomOfPage || !query.hasNextPage || query.isFetching) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void query.fetchNextPage()
      },
      { threshold: 0.5 },
    )

    observer.observe(bottomOfPage)
    return () => observer.disconnect()
  })

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/home`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Kembali">
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Rencana</h1>
      </div>
      <div>
        <Button
          href={`/${prefix}/activity-plan/create`}
          variant="ghost"
          size="icon"
          class="rounded-full">
          <PlusIcon aria-hidden="true"/>
        </Button>
      </div>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <Button
        variant="outline"
        size="icon"
        class="group"
        data-loading={query.isFetching}
        disabled={query.isFetching}
        onclick={() => void query.refetch()}>
        <LoaderCircleIcon class="invisible absolute animate-spin group-data-[loading=true]:visible"/>
        <RefreshCwIcon class="group-data-[loading=true]:invisible"/>
      </Button>
      <Input
        placeholder="Cari..."
        value={search.state}
        oninput={(event) => search.set(event.currentTarget.value)}/>
      <div class="flex-1"></div>
      <Button
        variant={filter.state.today ? "default" : "outline"}
        size="icon"
        onclick={() => filter.set("today", !filter.state.today)}>
        <Calendar1Icon aria-hidden="true"/>
      </Button>
      <Popover>
        <PopoverTrigger>
          {#snippet child({props})}
            <Button
              {...props}
              variant="outline"
              aria-label="Filter kategori">
              <FilterIcon aria-hidden="true"/>
            </Button>
          {/snippet}
        </PopoverTrigger>
        <PopoverContent
          align="end"
          class="relative grid w-72 gap-4 p-4"
          onOpenAutoFocus={(event) => event.preventDefault()}>
          <div class="space-y-1">
            <Label>Kategori</Label>
            <Popover>
              <PopoverButton
                class="w-full min-w-0"
                value={filter.state.category.name || "Semua Kategori"}/>
              <PopoverContent>
                <Command>
                  <CommandList class="min-h-20">
                    <CommandEmpty>Tidak ada data</CommandEmpty>
                    <CommandGroup>
                      {#each categories.data ?? [] as category (category.id)}
                        <CommandItem
                          value={category.name}
                          onclick={() => {
                            filter.set("category", category)
                            document.keyEsc()
                          }}>
                          {category.name}
                        </CommandItem>
                      {/each}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </nav>
  <div class="min-h-full">
    {#if query.isFetching && rows.isEmpty()}
      <div class="p-4 text-center text-xs">Memuat...</div>
    {:else if query.isError}
      <div class="p-4 text-center text-xs">
        <Button
          variant="outline"
          onclick={() => void query.refetch()}>
          Muat Ulang
        </Button>
      </div>
    {:else if rows.isEmpty()}
      <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
    {:else}
      {#each rows as activity (activity.id)}
        <div class="flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted">
          <div class="flex-1">
            <div class="flex items-center gap-1 text-xs text-muted-foreground">
              <div class="tabular-nums tracking-tight">{activity.date.toDateFormat("dd/MM/yyyy")}</div>
              <div class="text-[0.5rem]">|</div>
              <div class="tabular-nums tracking-tight">
                {activity.at_start.toDateFormat("HH:mm")} s.d. {activity.at_end.toDateFormat("HH:mm")}
              </div>
              <div class="flex-1"></div>
              <div
                class="flex-wrap"
                class:text-green-500={Boolean(activity.approval_status.id)}>
                {activity.approval_status.name || "diajukan"}
              </div>
            </div>
            <div>{activity.customer.name}</div>
            <div>{activity.name}</div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger>
              {#snippet child({props})}
                <Button
                  {...props}
                  variant="ghost"
                  size="icon-sm"
                  class="w-4">
                  <EllipsisVerticalIcon aria-hidden="true"/>
                </Button>
              {/snippet}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onclick={() => handleDetail(activity)}>
                Detail
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={activity.approval_status.id == 1}
                onclick={() => handleEdit(activity)}>
                Ubah
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      {/each}
    {/if}
    <div
      bind:this={bottomOfPage}
      class="h-1">
    </div>
    <div class="h-16"></div>
  </div>
</main>
<DialogDetail
  open={detailDialog.state}
  setOpen={detailDialog.setOpen}
  select={selected}/>
